import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import type { Meeting } from '@penticton-dev-events/shared';
import { brandMessaging } from '@penticton-dev-events/shared';

import { useAuth } from '../app/providers/AuthProvider';
import { PageContainer, Hero, VideoHero, Section } from '../components/layout';
import UpcomingMeetings from '../components/UpcomingMeetings';
import SEOHelmet from '../components/seo/SEOHelmet';
import { BrandSectionDivider } from '../components/brand';
import { useAllMeetings } from '../hooks/useAllMeetings';
import { generateWebSiteSchema } from '../lib/seo/structured-data';
import { useAnalytics } from '../hooks/useAnalytics';
import AuthDialog from '../features/auth/components/AuthDialog';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { meetings, loading, error } = useAllMeetings();
  const { trackViewGroup } = useAnalytics();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  const handleMeetingClick = (meeting: Meeting) => {
    navigate(`/meetings/${meeting.id}`);
  };

  const handleCreateGroup = () => {
    if (!user) {
      setAuthDialogOpen(true);
      return;
    }
    trackViewGroup('all-groups', 'All Groups', 'home-page');
    navigate('/groups');
  };

  const websiteSchema = generateWebSiteSchema();

  return (
    <PageContainer>
      <SEOHelmet
        description={
          brandMessaging.community.callToAction + '. ' + brandMessaging.community.mission
        }
        keywords={brandMessaging.seo.keywords.join(', ')}
        structuredData={websiteSchema}
      />
      {/* Hero Section */}
      <VideoHero
        title={brandMessaging.community.welcome}
        subtitle={brandMessaging.community.description}
        videoSrc="/hero_graph_new_54.mp4"
        actions={[
          {
            label: user ? 'Explore Groups' : 'Join the Community',
            onClick: handleCreateGroup,
            variant: 'contained',
            startIcon: <AddIcon />,
          },
          {
            label: 'Learn More',
            onClick: () => navigate('/about'),
            variant: 'outlined',
          },
        ]}
      />

      {/* Upcoming Meetings Section */}
      <Section variant="default">
        <Stack spacing={4}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom>
              Upcoming Events
            </Typography>
            <Typography color="text.secondary" paragraph>
              Don&apos;t miss out on the latest developer events in our community.
            </Typography>
            <BrandSectionDivider width="120px" />
          </Box>

          {error && (
            <Alert severity="error">
              Failed to load upcoming events: {error.message}
            </Alert>
          )}

          <UpcomingMeetings
            meetings={meetings}
            loading={loading}
            onMeetingClick={handleMeetingClick}
            showGroupName={true}
            maxMeetings={6}
          />

          {!loading && meetings.length > 0 && (
            <Box textAlign="center">
              <Button component={RouterLink} to="/events" variant="outlined" size="large">
                View All Groups & Events
              </Button>
            </Box>
          )}
        </Stack>
      </Section>

      {/* Call to Action for Organizers */}
      {profile?.isAdmin && (
        <Section variant="compact" background="paper">
          <Box textAlign="center">
            <Typography variant="h5" gutterBottom>
              Ready to Organize?
            </Typography>
            <Typography color="text.secondary" paragraph sx={{ mb: 3 }}>
              Create events, manage members, and send notifications to your community.
            </Typography>
            <BrandSectionDivider width="100px" showAccents={false} />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              justifyContent="center"
              sx={{ mt: 4 }}
            >
              <Button
                component={RouterLink}
                to="/groups"
                variant="contained"
                size="large"
              >
                Manage Groups
              </Button>
              <Button
                component={RouterLink}
                to="/broadcast"
                variant="outlined"
                size="large"
              >
                Send Announcement
              </Button>
            </Stack>
          </Box>
        </Section>
      )}

      {/* Guest Call to Action */}
      {!user && (
        <Hero
          title="Join Our Community"
          subtitle="Sign in to RSVP to meetings, join groups, and get notified about upcoming events in the Okanagan Valley."
          backgroundVariant="subtle"
          variant="compact"
          actions={[
            {
              label: 'Sign In',
              onClick: () => setAuthDialogOpen(true),
              variant: 'contained',
              size: 'large',
            },
          ]}
        />
      )}

      {/* Auth Dialog */}
      <AuthDialog
        open={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
        initialMode="signup"
      />
    </PageContainer>
  );
};

export default HomePage;
